/**
 * LexiaAvatar — reusable avatar for the Lexia coach.
 *
 * Ships responsive WebP + JPEG fallback via <picture>. The 256px asset
 * (~12 KB WebP / ~19 KB JPEG) is enough for every current use — the FAB
 * is 48-56 px, the header 32-36 px, the landing card 60-80 px.
 *
 * The terracotta ring animates when the widget is listening / speaking to
 * signal state at a glance.
 */
import avatar256Webp from "@/assets/lexia/avatar-256.webp";
import avatar256Jpg from "@/assets/lexia/avatar-256.jpg";
import avatar512Webp from "@/assets/lexia/avatar-512.webp";
import avatar512Jpg from "@/assets/lexia/avatar-512.jpg";

const SIZE = {
    xs: "w-8 h-8",
    sm: "w-10 h-10",
    md: "w-14 h-14",
    lg: "w-20 h-20",
    xl: "w-28 h-28",
};

// Bigger sizes cross the 256px @1x threshold — use 512 to stay crisp on
// retina displays.
const USE_LARGE = new Set(["xl"]);

/**
 * @param {object} props
 * @param {"xs"|"sm"|"md"|"lg"|"xl"} [props.size]
 * @param {"idle"|"listening"|"speaking"|"connecting"|"error"|"static"} [props.state]
 * @param {string} [props.className]
 * @param {string} [props.altOverride]
 * @param {boolean} [props.priority] — set true to eager-load (above-the-fold, modals)
 */
export default function LexiaAvatar({
    size = "md",
    state = "static",
    className = "",
    altOverride,
    priority = false,
}) {
    const dim = SIZE[size] || SIZE.md;
    const large = USE_LARGE.has(size);
    const webp = large ? avatar512Webp : avatar256Webp;
    const jpg = large ? avatar512Jpg : avatar256Jpg;
    const ring =
        state === "listening" ? "ring-emerald ring-2 animate-pulse"
            : state === "speaking" ? "ring-terracotta ring-2 animate-pulse"
                : state === "connecting" ? "ring-inkSoft ring-2"
                    : state === "error" ? "ring-terracotta ring-2"
                        : "ring-terracotta/60 ring-2";
    return (
        <span
            className={`relative inline-block rounded-full ${dim} ${ring} ring-offset-2 ring-offset-white ${className}`}
            data-testid="lexia-avatar"
            data-state={state}
        >
            <picture>
                <source srcSet={webp} type="image/webp" />
                <img
                    src={jpg}
                    alt={altOverride || "Lexia, votre coach linguistique"}
                    aria-label={altOverride || "Lexia, votre coach linguistique"}
                    loading={priority ? "eager" : "lazy"}
                    decoding="async"
                    fetchPriority={priority ? "high" : "auto"}
                    className="w-full h-full rounded-full object-cover"
                    draggable={false}
                />
            </picture>
        </span>
    );
}

export { avatar256Webp as lexiaAvatarWebp, avatar256Jpg as lexiaAvatarJpg };
